import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import { Calendar, User, Tag, ArrowLeft, ArrowRight, Share2 } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";

const API = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

/**
 * Markdown → HTML minimo, sicuro:
 * - h2 `## ` / h3 `### `
 * - **bold**
 * - liste con "- " / "1. "
 * - paragrafi separati da riga vuota
 */
function escapeHtml(s) {
  return (s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function renderContent(raw) {
  if (!raw) return "";
  const trimmed = raw.trim();
  // Detect HTML (nuovo editor Quill) vs Markdown (retrocompatibilità articoli seed)
  const isHtml = /<(p|h[1-6]|ul|ol|li|blockquote|strong|em|a|img|pre|code|br)[\s>]/i.test(trimmed);
  if (isHtml) {
    return DOMPurify.sanitize(raw, {
      ALLOWED_TAGS: [
        "p", "br", "h2", "h3", "h4", "strong", "em", "u", "s",
        "ul", "ol", "li", "blockquote", "a", "img", "pre", "code",
        "span", "div",
      ],
      ALLOWED_ATTR: ["href", "target", "rel", "src", "alt", "title", "class", "style"],
      ALLOW_DATA_ATTR: false,
      ADD_ATTR: ["target"],
    });
  }
  // Fallback markdown minimo (articoli storici / seed)
  return renderMarkdown(raw);
}

function renderMarkdown(md) {
  if (!md) return "";
  const escaped = escapeHtml(md);
  const blocks = escaped.split(/\n{2,}/);
  const html = blocks
    .map((raw) => {
      const block = raw.trim();
      if (!block) return "";
      if (block.startsWith("## ")) {
        return `<h2 class="mt-10 mb-4 text-2xl md:text-3xl font-extrabold text-[#0a1f55]">${block
          .slice(3)
          .trim()}</h2>`;
      }
      if (block.startsWith("### ")) {
        return `<h3 class="mt-8 mb-3 text-xl font-extrabold text-[#102a6b]">${block
          .slice(4)
          .trim()}</h3>`;
      }
      // liste unordered
      if (/^(-\s+)/.test(block)) {
        const items = block
          .split("\n")
          .map((l) => l.replace(/^-\s+/, "").trim())
          .filter(Boolean)
          .map((li) => `<li>${li.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")}</li>`)
          .join("");
        return `<ul class="my-4 list-disc pl-6 space-y-2 text-[15.5px] text-slate-700 leading-relaxed">${items}</ul>`;
      }
      // liste ordered
      if (/^(\d+\.\s+)/.test(block)) {
        const items = block
          .split("\n")
          .map((l) => l.replace(/^\d+\.\s+/, "").trim())
          .filter(Boolean)
          .map((li) => `<li>${li.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")}</li>`)
          .join("");
        return `<ol class="my-4 list-decimal pl-6 space-y-2 text-[15.5px] text-slate-700 leading-relaxed">${items}</ol>`;
      }
      const p = block.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
      return `<p class="my-4 text-[16px] text-slate-700 leading-[1.75]">${p.replace(/\n/g, "<br/>")}</p>`;
    })
    .join("\n");
  return html;
}

export default function BlogArticle() {
  const { slug } = useParams();
  const [articolo, setArticolo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    setLoading(true);
    setError(false);
    axios
      .get(`${API}/api/blog/${slug}`)
      .then((r) => setArticolo(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: articolo?.titolo,
          text: articolo?.excerpt,
          url: shareUrl,
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast({ description: "Link copiato negli appunti." });
      }
    } catch {
      /* utente ha annullato */
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-24 text-center text-slate-500">
          Caricamento articolo…
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !articolo) {
    return (
      <div className="min-h-screen bg-white">
        <SEO title="Articolo non trovato — A.C.D.T." description="L'articolo che stai cercando non è disponibile." />
        <Navbar />
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-24 text-center">
          <h1 className="text-3xl font-extrabold text-[#0a1f55]">Articolo non trovato</h1>
          <p className="mt-3 text-slate-600">
            L&apos;articolo che stai cercando non è più disponibile.
          </p>
          <Link to="/blog">
            <Button
              data-testid="blog-back-list"
              className="mt-6 bg-[#0a1f55] hover:bg-[#102a6b]"
            >
              <ArrowLeft className="mr-2 h-4 w-4" /> Torna al Blog
            </Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: articolo.titolo,
    description: articolo.seo_description || articolo.excerpt || "",
    image: articolo.og_image || articolo.cover_url || undefined,
    datePublished: articolo.pubblicato_at,
    dateModified: articolo.updated_at,
    author: {
      "@type": "Organization",
      name: articolo.autore || "A.C.D.T.",
    },
    publisher: {
      "@type": "Organization",
      name: "A.C.D.T. — Associazione Consulenti Digitali",
      logo: {
        "@type": "ImageObject",
        url: "https://www.assoacdt.com/acdt-logo.jpg",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://www.assoacdt.com/blog/${articolo.slug}`,
    },
  };

  return (
    <div className="relative min-h-screen bg-white" data-testid="blog-article">
      <SEO
        title={articolo.seo_title || `${articolo.titolo} — A.C.D.T.`}
        description={articolo.seo_description || articolo.excerpt}
        image={articolo.og_image || articolo.cover_url || undefined}
        jsonLd={jsonLd}
      />
      <Navbar />

      {/* Hero */}
      <article className="relative">
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0a1f55] via-[#102a6b] to-[#1e3a8a]" />
          {(articolo.cover_url || articolo.og_image) && (
            <img
              src={articolo.cover_url || articolo.og_image}
              alt={articolo.titolo}
              className="absolute inset-0 w-full h-full object-cover opacity-20"
            />
          )}
          <div className="relative max-w-4xl mx-auto px-5 md:px-8 py-16 md:py-20 text-white">
            <Link
              to="/blog"
              className="inline-flex items-center gap-1.5 text-[#fbbf24] text-sm font-semibold hover:underline"
              data-testid="blog-back-list"
            >
              <ArrowLeft className="h-4 w-4" /> Blog & News
            </Link>
            {articolo.categoria && (
              <div className="mt-4 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#f59e0b]/20 text-[#fbbf24] text-[11px] font-extrabold uppercase tracking-[0.2em]">
                {articolo.categoria}
              </div>
            )}
            <h1
              data-testid="blog-article-title"
              className="mt-4 text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight max-w-3xl"
            >
              {articolo.titolo}
            </h1>
            {articolo.sottotitolo && (
              <p className="mt-4 text-lg text-slate-200 max-w-3xl leading-relaxed">
                {articolo.sottotitolo}
              </p>
            )}
            <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-slate-200">
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="h-4 w-4" /> {formatDate(articolo.pubblicato_at)}
              </span>
              {articolo.autore && (
                <span className="inline-flex items-center gap-1.5">
                  <User className="h-4 w-4" /> {articolo.autore}
                </span>
              )}
              <button
                onClick={handleShare}
                data-testid="blog-share-btn"
                className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-bold border border-white/20"
              >
                <Share2 className="h-3.5 w-3.5" /> Condividi
              </button>
            </div>
          </div>
        </div>

        {/* Contenuto */}
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-12 md:py-16">
          <div
            data-testid="blog-article-content"
            className="prose-acdt"
            dangerouslySetInnerHTML={{ __html: renderContent(articolo.contenuto) }}
          />

          {articolo.tags && articolo.tags.length > 0 && (
            <div className="mt-10 pt-6 border-t border-slate-200 flex flex-wrap gap-2">
              {articolo.tags.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold"
                >
                  <Tag className="h-3 w-3" /> {t}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Correlati */}
        {articolo.correlati && articolo.correlati.length > 0 && (
          <div className="bg-gradient-to-b from-white to-[#f5f7ff] py-14 md:py-16">
            <div className="max-w-6xl mx-auto px-5 md:px-8">
              <div className="text-xs font-bold tracking-[0.22em] uppercase text-[#f59e0b]">
                Articoli correlati
              </div>
              <h2 className="mt-2 text-2xl md:text-3xl font-extrabold text-[#0a1f55]">
                Continua a leggere
              </h2>
              <div className="mt-8 grid md:grid-cols-3 gap-6">
                {articolo.correlati.map((c) => (
                  <Link
                    key={c.id}
                    to={`/blog/${c.slug}`}
                    data-testid={`blog-related-${c.slug}`}
                    className="group flex flex-col rounded-2xl border border-slate-200 bg-white p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all"
                  >
                    <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#f59e0b]">
                      {c.categoria || "News"}
                    </div>
                    <h3 className="mt-2 text-base font-extrabold text-[#0a1f55] leading-snug flex-1">
                      {c.titolo}
                    </h3>
                    {c.excerpt && (
                      <p className="mt-2 text-[13px] text-slate-600 leading-relaxed line-clamp-2">
                        {c.excerpt}
                      </p>
                    )}
                    <div className="mt-3 inline-flex items-center gap-1 text-[#f59e0b] text-sm font-bold group-hover:gap-2 transition-all">
                      Leggi <ArrowRight className="h-4 w-4" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* CTA finale */}
        <div className="bg-[#0a1f55] py-14 md:py-16 text-white">
          <div className="max-w-3xl mx-auto px-5 md:px-8 text-center">
            <h2 className="text-2xl md:text-3xl font-extrabold leading-tight">
              Diventa Consulente Digitale Certificato A.C.D.T.
            </h2>
            <p className="mt-3 text-slate-200 text-[15px]">
              Iscriviti al Corso A.C.D.T. per la qualificazione professionale ufficiale ex Legge 4/2013 e
              accedi al Registro Nazionale.
            </p>
            <Link to="/corso" data-testid="blog-cta-corso">
              <Button className="mt-6 bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1f55] font-bold px-6 py-6 text-base">
                Scopri il Corso <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}
